import React, { createContext, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { useApolloClient, useQuery } from '@apollo/client'

import {
  USER_GET_BUY_ORDER_BY_INTERNAL_KEY,
  USER_GET_SELL_ORDER_BY_INTERNAL_KEY,
  USER_UNREAD_NOTIFICATION_STATUS
} from '@/constants/graphql/queries'
import { AuthContext } from '@/stores/auth'
import mobileStore from '@/stores/store'
import { webStore } from '@/stores/webStore'

// Create a context for the notifications
export const NotificationsContext = createContext<{
  hasNewNotifications: boolean
  hasUnread: boolean
  markVisited: () => void
  numActionableSellOrders: number
  numActionableBuyOrders: number
  refresh: () => void
}>({
  hasNewNotifications: false,
  hasUnread: false,
  markVisited: () => {},
  numActionableSellOrders: 0,
  numActionableBuyOrders: 0,
  refresh: () => {}
})

export const ActionableOrderNotificationsContext = createContext({
  numActionableSellOrders: 0,
  numActionableBuyOrders: 0
})

interface providerProps {
  children: ReactNode
}

const store = Platform.OS === 'web' ? webStore : mobileStore

type NotificationState = {
  hasNewNotifications: boolean
  hasUnread: boolean
  lastUnreadId: string
  numActionableSellOrders: number
  numActionableBuyOrders: number
}

type NotificationStatus = {
  numNewUnread: number
  latestUnreadId?: string
  unreadExists: boolean
  numActionableSellOrders: number
  numActionableBuyOrders: number
}

type RawNotificationStatus = {
  numNewUnread?: string | number | null
  latestUnreadId?: string | null
  unreadExists?: string | number | boolean | null
  numActionableSellOrders?: string | number | null
  numActionableBuyOrders?: string | number | null
}

const initialState: NotificationState = {
  hasNewNotifications: false,
  hasUnread: false,
  lastUnreadId: '-1',
  numActionableSellOrders: 0,
  numActionableBuyOrders: 0
}

export function NotificationsProvider({ children }: providerProps) {
  const client = useApolloClient()
  const id = AuthContext.useSelector((state) => state.context.values?.user.id)
  const loggedIn = AuthContext.useSelector((state) => state.matches('LOGGED_IN'))
  const [state, setState] = useState<NotificationState>(initialState)
  const [hydratedUserId, setHydratedUserId] = useState<string>()

  const markVisited = useCallback(() => {
    setState((current) =>
      current.hasNewNotifications ? { ...current, hasNewNotifications: false } : current
    )
  }, [])

  const refresh = useCallback(() => {
    void client.refetchQueries({ include: [USER_UNREAD_NOTIFICATION_STATUS] })
  }, [client])

  const applyStatus = useCallback((status: NotificationStatus) => {
    setState((current) => {
      const next = {
        hasNewNotifications: status.numNewUnread > 0 || current.hasNewNotifications,
        hasUnread: status.numNewUnread > 0 ? status.unreadExists : current.hasUnread,
        lastUnreadId: status.numNewUnread > 0 ? (status.latestUnreadId ?? current.lastUnreadId) : current.lastUnreadId,
        numActionableSellOrders: status.numActionableSellOrders,
        numActionableBuyOrders: status.numActionableBuyOrders
      }

      return Object.entries(next).every(([key, value]) => current[key as keyof NotificationState] === value)
        ? current
        : next
    })
  }, [])

  const value = useMemo(
    () => ({
      hasNewNotifications: state.hasNewNotifications,
      markVisited,
      hasUnread: state.hasUnread,
      numActionableSellOrders: state.numActionableSellOrders,
      numActionableBuyOrders: state.numActionableBuyOrders,
      refresh
    }),
    [markVisited, refresh, state]
  )

  const actionableOrderValue = useMemo(
    () => ({
      numActionableSellOrders: state.numActionableSellOrders,
      numActionableBuyOrders: state.numActionableBuyOrders
    }),
    [state.numActionableBuyOrders, state.numActionableSellOrders]
  )

  useEffect(() => {
    let active = true

    setHydratedUserId(undefined)
    setState(initialState)
    if (!loggedIn || !id) {
      return () => {
        active = false
      }
    }

    void store
      .getNotificationsStatus(id)
      .then((settings) => {
        if (!active) return

        setState({
          ...initialState,
          hasNewNotifications: isUnread(settings?.hasUnread),
          hasUnread: isUnread(settings?.hasUnread),
          lastUnreadId: settings?.lastUnreadId ?? '-1'
        })
        setHydratedUserId(id)
      })
      .catch((error) => {
        console.error('Failed to restore notification status: ', error)
        if (active) setHydratedUserId(id)
      })

    return () => {
      active = false
    }
  }, [id, loggedIn])

  useEffect(() => {
    if (!loggedIn || hydratedUserId !== id || !id) return

    void store.setNotificationsStatus(id, {
      hasUnread: state.hasUnread,
      lastUnreadId: state.lastUnreadId
    })
  }, [hydratedUserId, id, loggedIn, state.hasUnread, state.lastUnreadId])

  return (
    <ActionableOrderNotificationsContext.Provider value={actionableOrderValue}>
      <NotificationsContext.Provider value={value}>
        {loggedIn && hydratedUserId === id && id ? (
          <NotificationPollingService
            key={id}
            lastUnreadId={state.lastUnreadId}
            onStatus={applyStatus}
          />
        ) : null}
        {children}
      </NotificationsContext.Provider>
    </ActionableOrderNotificationsContext.Provider>
  )
}

function NotificationPollingService({
  lastUnreadId,
  onStatus
}: {
  lastUnreadId: string
  onStatus: (status: NotificationStatus) => void
}) {
  const client = useApolloClient()
  const handledData = useRef<unknown>(undefined)
  const { data, error } = useQuery(USER_UNREAD_NOTIFICATION_STATUS, {
    variables: { lastId: lastUnreadId },
    pollInterval: 20_000,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false
  })

  useEffect(() => {
    if (!data || handledData.current === data) return
    handledData.current = data

    const rawStatus: RawNotificationStatus = data.userUnreadNotificationStatus
    const status: NotificationStatus = {
      numNewUnread: toCount(rawStatus.numNewUnread),
      latestUnreadId: rawStatus.latestUnreadId ?? undefined,
      unreadExists: isUnread(rawStatus.unreadExists),
      numActionableSellOrders: toCount(rawStatus.numActionableSellOrders),
      numActionableBuyOrders: toCount(rawStatus.numActionableBuyOrders)
    }
    if (
      status.numNewUnread > 0 &&
      status.latestUnreadId &&
      lastUnreadId !== '-1' &&
      status.latestUnreadId !== lastUnreadId
    ) {
      void client.refetchQueries({
        include: [USER_GET_BUY_ORDER_BY_INTERNAL_KEY, USER_GET_SELL_ORDER_BY_INTERNAL_KEY]
      })
    }

    onStatus(status)
  }, [client, data, lastUnreadId, onStatus])

  useEffect(() => {
    if (error) console.error('Failed to poll notifications: ', error)
  }, [error])

  return null
}

function toCount(value: string | number | null | undefined) {
  const count = Number(value ?? 0)
  return Number.isFinite(count) ? count : 0
}

function isUnread(value: string | number | boolean | null | undefined) {
  return value === true || (value !== false && Number(value ?? 0) > 0)
}

export default NotificationsProvider
